/**
 * Formatowanie współrzędnych otworu do WYŚWIETLENIA na karcie w wybranym układzie.
 *
 * Dane są zawsze przechowywane jako WGS84 (lat/lng). Tu konwertujemy je do:
 *   - WGS84       — stopnie dziesiętne (φ N, λ E),
 *   - PUWG_2000   — układ 2000 (EPSG:2176–2179, strefa auto z długości), X=northing, Y=easting,
 *   - PUWG_1992   — układ 1992 (EPSG:2180), X=northing, Y=easting.
 *
 * W polskiej geodezji oś X biegnie na PÓŁNOC (northing), a oś Y na WSCHÓD (easting) —
 * odwrotnie niż w konwencji matematycznej/GIS. Dlatego etykiety X/Y są zamienione względem
 * kolejności [easting, northing] zwracanej przez proj4.
 */
import proj4 from 'proj4';
import { wgs84ToPuwg2000 } from './puwg2000';

export type CoordinateSystem = 'WGS84' | 'PUWG_2000' | 'PUWG_1992';

/** Etykiety układów do UI (selektor w edytorze karty). */
export const COORDINATE_SYSTEM_OPTIONS: Array<{ value: CoordinateSystem; label: string }> = [
  { value: 'WGS84', label: 'WGS84 (φ, λ — stopnie)' },
  { value: 'PUWG_2000', label: 'Układ 2000 (X, Y — metry)' },
  { value: 'PUWG_1992', label: 'Układ 1992 / EPSG:2180 (X, Y — metry)' },
];

const WGS84_DEF = '+proj=longlat +datum=WGS84 +no_defs';
const PL_1992_DEF =
  '+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs';

/**
 * WGS84 → PL-1992 (EPSG:2180). Zwraca { x: northing, y: easting } w metrach albo null.
 */
export function wgs84ToPl1992(lat: number, lng: number): { x: number; y: number } | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  try {
    // proj4 zwraca [easting, northing] dla odwzorowania rzutowanego
    const [easting, northing] = proj4(WGS84_DEF, PL_1992_DEF, [lng, lat]) as [number, number];
    if (!Number.isFinite(easting) || !Number.isFinite(northing)) return null;
    return { x: northing, y: easting };
  } catch {
    return null;
  }
}

/** Metry z polskim przecinkiem, bez separatora tysięcy (jak na drukach geodezyjnych): „5790123,45". */
const meters = (n: number): string =>
  n.toLocaleString('pl-PL', { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Stopnie z polskim przecinkiem, 6 miejsc: „52,234560". */
const degrees = (n: number): string =>
  n.toLocaleString('pl-PL', { minimumFractionDigits: 6, maximumFractionDigits: 6 });

/**
 * Buduje etykietę współrzędnych dla nagłówka karty w wybranym układzie.
 * Zwraca '' gdy brak którejkolwiek współrzędnej. Przy nieudanej konwersji (poza PL / błąd proj4)
 * fallbackuje do WGS84, żeby na karcie zawsze była jakaś wartość.
 */
export function formatCoordinates(
  lat: number | null | undefined,
  lng: number | null | undefined,
  system: CoordinateSystem | null | undefined,
): string {
  if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return '';
  const sys = system ?? 'WGS84';

  if (sys === 'PUWG_2000') {
    const r = wgs84ToPuwg2000(lat, lng);
    if (r) return `X = ${meters(r.x)}; Y = ${meters(r.y)} (2000/${r.zone})`;
  } else if (sys === 'PUWG_1992') {
    const r = wgs84ToPl1992(lat, lng);
    if (r) return `X = ${meters(r.x)}; Y = ${meters(r.y)} (1992)`;
  }
  // WGS84 (domyślny + fallback po nieudanej konwersji)
  return `φ = ${degrees(lat)} N, λ = ${degrees(lng)} E`;
}
